import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as XLSX from "xlsx";
import { FileUploader } from "react-drag-drop-files";

import { TRACKER_UPLOAD_FILE_TYPES } from "@/utils/trackerUploadFileTypes";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { classNames } from "@/utils/classNames";
import { parseCsvTable } from "@/utils/parseCsvText";

import {
	useCreateTrackerFileMutation,
	useUpdateTrackerFileMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { useSuggestCsvMappingMutation } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { useSingleFlight } from "@/utils/useSingleFlight";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { InputField } from "@/components/InputField/InputField";
import { Spinner } from "@/components/Spinner/Spinner";

import {
	CreateTrackerFile,
	CreateTrackerFileSchema,
	Tracker,
	TrackerFile,
} from "../schema/profileTracker";

interface CreateXlsxFormProps {
	trackerFile?: TrackerFile | null;
	tracker: Tracker;
	handleSelect?: (trackerFIle: TrackerFile | null) => void;
}

const minimumFields = [
	// ["email"],
	// ["linkedinUrl"],
	// ["name", "companyName"],
	// ["title", "companyName"],
	// ["firstName", "lastName", "companyName"],
	// ["companyName"],
	["companyLinkedinUrl"],
	["name", "holdingLinkedinUrl"],
	["websiteUrl"],
	// ["companyDomain"],
];

const defaultOptions = [
	{ title: "Do Not import", id: "", compares: [] as string[] },
	{
		title: "Company Name",
		id: "name",
		compares: ["name", "companyName", "Company Name", "Company"],
	},
	{
		title: "Company Linkedin",
		id: "companyLinkedinUrl",
		compares: ["companyLinkedinUrl", "Company Linkedin", "Company LinkedIn URL"],
	},
	{
		title: "Holding Linkedin",
		id: "holdingLinkedinUrl",
		compares: ["holdingLinkedinUrl", "Holding Linkedin", "Parent Company Linkedin"],
	},
	{
		title: "Company Website",
		id: "websiteUrl",
		compares: ["websiteUrl", "Website", "Company Website", "Domain"],
	},
	{ title: "Save as Custom Field", id: "customField", compares: [] as string[] },
];

export const CreateXlsxForm: FunctionComponent<CreateXlsxFormProps> = ({
	trackerFile,
	tracker,
	handleSelect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.${key}`);
	const [createTrackerFile, { isLoading, error }] = useCreateTrackerFileMutation();
	const [updateTrackerFile, { isLoading: updateIsLoading, error: updateError }] =
		useUpdateTrackerFileMutation();
	const [suggestCsvMapping] = useSuggestCsvMappingMutation();
	const { run: runSubmit, isRunning } = useSingleFlight();
	const [fileContent, setFileContent] = useState<string>();
	const [originalFileName, setOriginalFileName] = useState<string>("");
	const [foundFields, setFoundFields] = useState<{ foundField: string; assignedField: string }[]>();
	const [isAiSuggesting, setIsAiSuggesting] = useState(false);

	const [allowUpload, setAllowUpload] = useState<boolean>(false);

	const [currentFormState, setCurrentFormState] = useState<CreateTrackerFile>({
		name: "",
		file: "",
		configuration: [],
	});

	const {
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<CreateTrackerFile>({
		defaultValues: {},
		resolver: zodResolver(CreateTrackerFileSchema),
	});

	const handleSaveFormState = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(value: any, name: keyof CreateTrackerFile) => {
			setCurrentFormState({
				...currentFormState,
				[name]: value,
			});

			setValue(name, value, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[currentFormState, setValue]
	);

	useEffect(() => {
		if (trackerFile) {
			reset({
				id: trackerFile.id,
				name: trackerFile.name,
				configuration: trackerFile.configuration,
			});

			handleSaveFormState(trackerFile.name, "name");

			setFoundFields(trackerFile.configuration);
		} else {
			reset();
		}
	}, [trackerFile, reset]);

	useEffect(() => {
		if (foundFields) {
			const isMinimumFields = minimumFields.some((field) => {
				return field.every((f) => foundFields.map((m) => m.assignedField).includes(f));
			});

			setAllowUpload(isMinimumFields);
		}
	}, [foundFields]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const submit = async (values: CreateTrackerFile) => {
		try {
			if (trackerFile) {
				await updateTrackerFile({
					id: trackerFile.id,
					trackerId: tracker.id,
					...values,
					configuration: JSON.stringify(foundFields),
				}).unwrap();
			} else {
				const { file: fileRaw, name } = values;

				if (fileRaw) {
					const formData = new FormData();
					const res: Response = await fetch(fileRaw);
					const blob: Blob = await res.blob();
					const file = new File([blob], originalFileName || "file.csv");

					formData.append("file", file);
					formData.append("name", name || originalFileName || "");
					formData.append("trackerId", tracker.id.toString());
					formData.append("type", "csv");
					formData.append("configuration", JSON.stringify(foundFields));

					await createTrackerFile(formData).unwrap();
				}
			}

			handleSelect?.(null);
			reset();
		} catch (err) {
			console.error(err);
		}
	};

	// Same shape as the CSV source form: the file is fetched and read into a Blob before the
	// create request starts, so a double click uploaded twice (PREC-4554).
	const onSubmit = (values: CreateTrackerFile) => runSubmit(() => submit(values));

	useEffect(() => {
		const processFoundFields = async (fileContent?: string) => {
			if (!fileContent) {
				setFoundFields(undefined);

				return;
			}

			const res = await fetch(fileContent);

			const buffer = await res.arrayBuffer();

			const workbook = XLSX.read(buffer);
			const content = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);

			// `sheet_to_csv` quotes any cell holding a comma, so the text has to be parsed rather
			// than split — see parseCsvText (PREC-3867).
			const { headers: fields, rows: dataRows } = parseCsvTable(content);
			const fieldsToConsider = fields.filter((field) => !!field);

			// Extract first 3 data rows (after header) for AI suggestion
			const sampleRows = dataRows.slice(0, 3);

			// Immediate fallback: use compares-based matching
			const fallbackFields: { foundField: string; assignedField: string }[] = fieldsToConsider.map(
				(field) => {
					const option = defaultOptions.find((o) => o.compares?.includes(field));

					return {
						foundField: field,
						assignedField: option?.id || "",
					};
				}
			);

			setFoundFields(fallbackFields);

			// Call AI suggestion endpoint in the background
			if (sampleRows.length > 0) {
				setIsAiSuggesting(true);

				try {
					const result = await suggestCsvMapping({
						entityType: CsvEnrichmentType.ORGANIZATION,
						headers: fieldsToConsider,
						sampleRows,
					}).unwrap();

					if (result.mapping?.length) {
						// Only apply AI suggestions for valid assignedField values
						const validFieldIds = defaultOptions.map((o) => o.id);

						const aiFields = fieldsToConsider.map((field) => {
							const aiMatch = result.mapping.find((m) => m.foundField === field);
							const assignedField = aiMatch?.assignedField || "";

							return {
								foundField: field,
								assignedField: validFieldIds.includes(assignedField) ? assignedField : "",
							};
						});

						setFoundFields(aiFields);
					}
				} catch {
					// AI suggestion failed — keep fallback mapping
				} finally {
					setIsAiSuggesting(false);
				}
			}
		};

		processFoundFields(fileContent);
	}, [fileContent]);

	const options = useMemo(() => {
		return defaultOptions.filter((a) => {
			if (a.id === "customField" || a.id === "") {
				return true;
			}

			if (foundFields?.map((m) => m.assignedField).includes(a.id)) {
				return false;
			}

			return true;
		});
	}, [foundFields]);

	console.log(errors);

	return (
		<div className="">
			<div className="text-md mb-5">Sources</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						if (event.target.value !== currentFormState?.name) {
							handleSaveFormState(event.target.value, "name");
						}
					}}
					label={ts("name")}
					name={"name"}
					placeholder={ts("namePlaceholder")}
					value={currentFormState?.name || ""}
				/>
			</div>
			<div className="flex flex-col gap-4 w-full mb-4">
				{fileContent || foundFields ? (
					<>
						{foundFields ? (
							<div className="flex items-center gap-2 mb-2">
								<div className="text-text text-left text-ssm font-semibold">
									{ts("foundFields")}
								</div>
								{isAiSuggesting && (
									<div className="flex items-center gap-1.5 text-text-meta text-xs">
										<Spinner color="#4d66f5" />
										<span>{t("csvEnrichment.aiAnalyzing")}</span>
									</div>
								)}
							</div>
						) : (
							""
						)}
						<div className="w-full">
							<ul>
								{foundFields
									?.filter((a) => a.foundField)
									?.map((item, index) => {
										const selectedField = defaultOptions.find((o) => {
											return o.id === item.assignedField;
										});

										return (
											<li
												key={`list-index-${index}`}
												className="text-xs flex flex-row mb-2 justify-between items-center"
											>
												<div className="text-sm mr-2">{item.foundField}</div>
												<div className="w-[350px]">
													<Dropdown
														classNameButton={classNames(
															selectedField?.id === "" ? "text-gray-200" : ""
														)}
														data={options}
														defaultValue={selectedField || { title: "Please select", id: "" }}
														floating={true}
														handleSelect={(value?: AutoCompleteItem) => {
															if (value) {
																setFoundFields([
																	...foundFields.slice(0, index),
																	{
																		foundField: item.foundField?.trim(),
																		assignedField: value?.id?.toString()?.trim() || "",
																	},
																	...foundFields.slice(index + 1),
																]);
															}
														}}
													/>
												</div>
											</li>
										);
									})}
							</ul>
						</div>
					</>
				) : (
					<FileUploader
						handleChange={(file: File | File[]) => {
							const singleFile = Array.isArray(file) ? file[0] : file;

							setOriginalFileName(singleFile.name);

							if (!currentFormState.name) {
								handleSaveFormState(singleFile.name, "name");
							}

							setValue("file", URL.createObjectURL(singleFile));
							setFileContent(URL.createObjectURL(singleFile));
						}}
						name="file"
						types={TRACKER_UPLOAD_FILE_TYPES}
					/>
				)}
			</div>
			<div className="flex flex-col w-full mb-4"></div>

			<div className="mb-2">
				{getAllErrors(error || updateError, formErrors).length
					? renderErrorMessages(getAllErrors(error || updateError, formErrors))
					: null}
			</div>
			<div className="flex gap-4 w-full justify-end">
				<div className="flex max-w-[100px] w-full">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title="cancel"
						onClick={() => {
							handleSelect?.(null);
							setFileContent(undefined);
							reset();
						}}
					/>
				</div>
				<div className="flex max-w-[150px] w-full">
					<Button
						disabled={!allowUpload}
						isLoading={isLoading || updateIsLoading || isRunning}
						title={trackerFile?.id ? "Save" : "Upload"}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
